
import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Phone, Mail, MapPin, Clock, MessageSquare, Send, CheckCircle } from 'lucide-react';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    budget: '',
    message: '',
    projectType: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Simulate API call
      await new Promise((resolve) => setTimeout(resolve, 1500));

      toast.success('¡Mensaje enviado exitosamente!', {
        description: 'Nos pondremos en contacto contigo en las próximas 24 horas.'
      });

      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        budget: '',
        message: '',
        projectType: ''
      });
    } catch (error) {
      toast.error('Error al enviar el mensaje', {
        description: 'Por favor intenta nuevamente o contáctanos directamente.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const contactInfo = [
  {
    icon: <Phone className="w-6 h-6" data-id="q1j4n7r0j" data-path="src/components/ContactSection.tsx" />,
    title: 'Teléfono',
    value: '55.6251.7774',
    subtitle: 'Lunes a Viernes 9AM - 7PM',
    color: 'from-blue-500 to-blue-600'
  },
  {
    icon: <Mail className="w-6 h-6" data-id="0jr60sh6v" data-path="src/components/ContactSection.tsx" />,
    title: 'Email',
    value: 'nathan@automatiza.me',
    subtitle: 'Respuesta en 24 horas',
    color: 'from-green-500 to-green-600'
  },
  {
    icon: <MapPin className="w-6 h-6" data-id="q87w6xcla" data-path="src/components/ContactSection.tsx" />,
    title: 'Ubicación',
    value: 'Ciudad de México',
    subtitle: 'Servicio en toda el área metropolitana',
    color: 'from-purple-500 to-purple-600'
  },
  {
    icon: <Clock className="w-6 h-6" data-id="x5etwpwzb" data-path="src/components/ContactSection.tsx" />,
    title: 'Horario',
    value: 'Lun - Vie: 9AM - 7PM',
    subtitle: 'Sáb: 10AM - 4PM',
    color: 'from-orange-500 to-orange-600'
  }];


  const services = [
  'Consulta y Evaluación',
  'Automatización Completa',
  'Iluminación Inteligente',
  'Seguridad del Hogar',
  'Climatización',
  'Entretenimiento/Sonos',
  'Oficina Inteligente',
  'Soporte Técnico',
  'Otro'];


  const budgetRanges = [
  'Menos de $50,000',
  '$50,000 - $100,000',
  '$100,000 - $200,000',
  '$200,000 - $500,000',
  'Más de $500,000',
  'Necesito cotización'];


  const projectTypes = [
  'Residencial - Casa',
  'Residencial - Departamento',
  'Oficina Pequeña',
  'Oficina Corporativa',
  'Comercial',
  'Industrial',
  'Otro'];


  return (
    <section id="contacto" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50" data-id="qjsppabhr" data-path="src/components/ContactSection.tsx">
      <div className="container mx-auto px-4" data-id="kucxdgdif" data-path="src/components/ContactSection.tsx">
        {/* Header */}
        <div className="text-center mb-16" data-id="filtdvjgf" data-path="src/components/ContactSection.tsx">
          <Badge className="bg-gradient-to-r from-blue-600 to-purple-600 text-white px-4 py-2 text-sm font-medium mb-4" data-id="ejbwaflhu" data-path="src/components/ContactSection.tsx">
            📞 Contáctanos
          </Badge>
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-id="lxkdugpr7" data-path="src/components/ContactSection.tsx">
            Comienza tu
            <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent" data-id="nygtczbb2" data-path="src/components/ContactSection.tsx"> Transformación</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed" data-id="fpdwpqb2e" data-path="src/components/ContactSection.tsx">
            Estamos listos para hacer realidad el hogar inteligente que siempre has soñado. 
            <span className="font-semibold text-blue-600" data-id="4pclison6" data-path="src/components/ContactSection.tsx"> ¡No esperes más!</span>
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 max-w-6xl mx-auto" data-id="yqtgausx8" data-path="src/components/ContactSection.tsx">
          {/* Contact Information */}
          <div className="space-y-8" data-id="78bxigjk3" data-path="src/components/ContactSection.tsx">
            <div data-id="smd3q3axd" data-path="src/components/ContactSection.tsx">
              <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="kh53zew5y" data-path="src/components/ContactSection.tsx">
                Información de Contacto
              </h3>
              <div className="grid gap-6" data-id="67fmw4x1f" data-path="src/components/ContactSection.tsx">
                {contactInfo.map((info, index) =>
                <Card key={index} className="hover:shadow-lg transition-shadow duration-300 border-0" data-id="ehm4tvry5" data-path="src/components/ContactSection.tsx">
                    <CardContent className="p-6" data-id="szto3aceh" data-path="src/components/ContactSection.tsx">
                      <div className="flex items-start space-x-4" data-id="zredwabyq" data-path="src/components/ContactSection.tsx">
                        <div className={`p-3 rounded-lg bg-gradient-to-r ${info.color} text-white`} data-id="mbxmvbmsn" data-path="src/components/ContactSection.tsx">
                          {info.icon}
                        </div>
                        <div className="flex-1" data-id="d7c6nw61p" data-path="src/components/ContactSection.tsx">
                          <h4 className="font-semibold text-gray-900 mb-1" data-id="gvydm08ko" data-path="src/components/ContactSection.tsx">{info.title}</h4>
                          <p className="text-lg text-gray-800 mb-1" data-id="p9c147sza" data-path="src/components/ContactSection.tsx">{info.value}</p>
                          <p className="text-sm text-gray-600" data-id="58ndxvo56" data-path="src/components/ContactSection.tsx">{info.subtitle}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>

            {/* Why Choose Us */}
            <div className="bg-white rounded-2xl p-8 shadow-lg" data-id="3u13o9du5" data-path="src/components/ContactSection.tsx">
              <h3 className="text-xl font-bold text-gray-900 mb-6" data-id="3aotiv91g" data-path="src/components/ContactSection.tsx">
                ¿Por Qué Elegirnos?
              </h3>
              <div className="space-y-4" data-id="jrsocqhgy" data-path="src/components/ContactSection.tsx">
                <div className="flex items-center space-x-3" data-id="dvtasbufs" data-path="src/components/ContactSection.tsx">
                  <CheckCircle className="w-5 h-5 text-green-500" data-id="h4ec4im24" data-path="src/components/ContactSection.tsx" />
                  <span className="text-gray-700" data-id="tl1jylf5v" data-path="src/components/ContactSection.tsx">25 años de experiencia en automatización</span>
                </div>
                <div className="flex items-center space-x-3" data-id="uok82373n" data-path="src/components/ContactSection.tsx">
                  <CheckCircle className="w-5 h-5 text-green-500" data-id="y6ztz6jjz" data-path="src/components/ContactSection.tsx" />
                  <span className="text-gray-700" data-id="omagtk68o" data-path="src/components/ContactSection.tsx">Precios más competitivos del mercado</span>
                </div>
                <div className="flex items-center space-x-3" data-id="9me3y74fb" data-path="src/components/ContactSection.tsx">
                  <CheckCircle className="w-5 h-5 text-green-500" data-id="vidsrowzy" data-path="src/components/ContactSection.tsx" />
                  <span className="text-gray-700" data-id="khvkm8n9s" data-path="src/components/ContactSection.tsx">Soporte técnico 24/7 incluido</span>
                </div>
                <div className="flex items-center space-x-3" data-id="1b0g0hiq5" data-path="src/components/ContactSection.tsx">
                  <CheckCircle className="w-5 h-5 text-green-500" data-id="dlmi3ca7o" data-path="src/components/ContactSection.tsx" />
                  <span className="text-gray-700" data-id="42vnsewwp" data-path="src/components/ContactSection.tsx">Garantía de satisfacción total</span>
                </div>
                <div className="flex items-center space-x-3" data-id="815kbhj82" data-path="src/components/ContactSection.tsx">
                  <CheckCircle className="w-5 h-5 text-green-500" data-id="17febbcs0" data-path="src/components/ContactSection.tsx" />
                  <span className="text-gray-700" data-id="lddexz933" data-path="src/components/ContactSection.tsx">Instalación y configuración profesional</span>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white" data-id="9kiydirkh" data-path="src/components/ContactSection.tsx">
              <h3 className="text-xl font-bold mb-4" data-id="xtpozstmt" data-path="src/components/ContactSection.tsx">¿Necesitas Ayuda Inmediata?</h3>
              <p className="mb-6 opacity-90" data-id="6jlfhvp15" data-path="src/components/ContactSection.tsx">
                Nuestro equipo está disponible para resolver tus dudas y comenzar tu proyecto hoy mismo.
              </p>
              <div className="flex flex-col sm:flex-row gap-4" data-id="rf4zju7g8" data-path="src/components/ContactSection.tsx">
                <Button
                  variant="secondary"
                  className="bg-white text-blue-600 hover:bg-gray-100 flex items-center space-x-2" data-id="m3aplihd9" data-path="src/components/ContactSection.tsx">

                  <Phone className="w-4 h-4" data-id="q7etla6ny" data-path="src/components/ContactSection.tsx" />
                  <span data-id="p2dmck46v" data-path="src/components/ContactSection.tsx">Llamar Ahora</span>
                </Button>
                <Button
                  variant="outline"
                  className="border-white text-white hover:bg-white hover:text-blue-600 flex items-center space-x-2" data-id="76k8slidu" data-path="src/components/ContactSection.tsx">

                  <MessageSquare className="w-4 h-4" data-id="dmkpv4gql" data-path="src/components/ContactSection.tsx" />
                  <span data-id="71xont9q6" data-path="src/components/ContactSection.tsx">WhatsApp</span>
                </Button>
              </div>
            </div>
          </div>

          {/* Contact Form */}
          <Card className="shadow-2xl border-0" data-id="ophpe5y71" data-path="src/components/ContactSection.tsx">
            <CardContent className="p-8" data-id="nwnxp9gsy" data-path="src/components/ContactSection.tsx">
              <div className="mb-6" data-id="2myi0s13a" data-path="src/components/ContactSection.tsx">
                <h3 className="text-2xl font-bold text-gray-900 mb-2" data-id="r33b0hfzz" data-path="src/components/ContactSection.tsx">
                  Recibe tu Cotización Gratuita
                </h3>
                <p className="text-gray-600" data-id="oxdbrr9ow" data-path="src/components/ContactSection.tsx">
                  Completa el formulario y nos pondremos en contacto contigo en las próximas 24 horas.
                </p>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6" data-id="r6g4vptgh" data-path="src/components/ContactSection.tsx">
                <div className="grid md:grid-cols-2 gap-4" data-id="uvgme0cx3" data-path="src/components/ContactSection.tsx">
                  <div className="space-y-2" data-id="2qgsxtaun" data-path="src/components/ContactSection.tsx">
                    <Label htmlFor="name" data-id="dtdm5y68z" data-path="src/components/ContactSection.tsx">Nombre completo *</Label>
                    <Input
                      id="name"
                      type="text"
                      required
                      placeholder="Tu nombre"
                      value={formData.name}
                      onChange={(e) => handleChange('name', e.target.value)} data-id="1qf1c2qsy" data-path="src/components/ContactSection.tsx" />

                  </div>
                  <div className="space-y-2" data-id="zr90x8xn9" data-path="src/components/ContactSection.tsx">
                    <Label htmlFor="phone" data-id="fngi59etv" data-path="src/components/ContactSection.tsx">Teléfono *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      placeholder="55 1234 5678"
                      value={formData.phone}
                      onChange={(e) => handleChange('phone', e.target.value)} data-id="rd6mxqok9" data-path="src/components/ContactSection.tsx" />

                  </div>
                </div>

                <div className="space-y-2" data-id="h54ynj6s2" data-path="src/components/ContactSection.tsx">
                  <Label htmlFor="email" data-id="oa2j2l9ue" data-path="src/components/ContactSection.tsx">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    required
                    placeholder="tu@email.com"
                    value={formData.email}
                    onChange={(e) => handleChange('email', e.target.value)} data-id="ktr9sjoin" data-path="src/components/ContactSection.tsx" />

                </div>

                <div className="grid md:grid-cols-2 gap-4" data-id="ljixqg36t" data-path="src/components/ContactSection.tsx">
                  <div className="space-y-2" data-id="92w5hmn06" data-path="src/components/ContactSection.tsx">
                    <Label htmlFor="service" data-id="ieukrz2rz" data-path="src/components/ContactSection.tsx">Servicio de interés</Label>
                    <Select value={formData.service} onValueChange={(value) => handleChange('service', value)} data-id="ghmrh06i0" data-path="src/components/ContactSection.tsx">
                      <SelectTrigger data-id="n9aykqzcj" data-path="src/components/ContactSection.tsx">
                        <SelectValue placeholder="Selecciona un servicio" data-id="s1ep3k5uf" data-path="src/components/ContactSection.tsx" />
                      </SelectTrigger>
                      <SelectContent data-id="mvh3g3i7k" data-path="src/components/ContactSection.tsx">
                        {services.map((service) =>
                        <SelectItem key={service} value={service} data-id="8uybaksea" data-path="src/components/ContactSection.tsx">{service}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2" data-id="sdfx4xnkz" data-path="src/components/ContactSection.tsx">
                    <Label htmlFor="projectType" data-id="qmq9glsmx" data-path="src/components/ContactSection.tsx">Tipo de proyecto</Label>
                    <Select value={formData.projectType} onValueChange={(value) => handleChange('projectType', value)} data-id="9o91hies0" data-path="src/components/ContactSection.tsx">
                      <SelectTrigger data-id="4xdvgk8i0" data-path="src/components/ContactSection.tsx">
                        <SelectValue placeholder="Selecciona tipo" data-id="47sql01l3" data-path="src/components/ContactSection.tsx" />
                      </SelectTrigger>
                      <SelectContent data-id="r0fyuic08" data-path="src/components/ContactSection.tsx">
                        {projectTypes.map((type) =>
                        <SelectItem key={type} value={type} data-id="ypfeyi38h" data-path="src/components/ContactSection.tsx">{type}</SelectItem>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="space-y-2" data-id="fblcjsbhu" data-path="src/components/ContactSection.tsx">
                  <Label htmlFor="budget" data-id="ab06vgxiw" data-path="src/components/ContactSection.tsx">Presupuesto estimado</Label>
                  <Select value={formData.budget} onValueChange={(value) => handleChange('budget', value)} data-id="akkps8hcy" data-path="src/components/ContactSection.tsx">
                    <SelectTrigger data-id="iccnjys0n" data-path="src/components/ContactSection.tsx">
                      <SelectValue placeholder="Selecciona rango de presupuesto" data-id="b4rsboqts" data-path="src/components/ContactSection.tsx" />
                    </SelectTrigger>
                    <SelectContent data-id="am6axvjzz" data-path="src/components/ContactSection.tsx">
                      {budgetRanges.map((range) =>
                      <SelectItem key={range} value={range} data-id="equw1zfac" data-path="src/components/ContactSection.tsx">{range}</SelectItem>
                      )}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2" data-id="gzpdm8v11" data-path="src/components/ContactSection.tsx">
                  <Label htmlFor="message" data-id="kdufdsk13" data-path="src/components/ContactSection.tsx">Mensaje</Label>
                  <Textarea
                    id="message"
                    placeholder="Cuéntanos sobre tu proyecto, necesidades específicas o cualquier pregunta..."
                    rows={4}
                    value={formData.message}
                    onChange={(e) => handleChange('message', e.target.value)} data-id="k72s2jbhg" data-path="src/components/ContactSection.tsx" />

                </div>

                <Button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white py-3 text-lg font-medium" data-id="g8cyuyzw8" data-path="src/components/ContactSection.tsx">

                  {isSubmitting ?
                  <div className="flex items-center space-x-2" data-id="ju3xx8gp4" data-path="src/components/ContactSection.tsx">
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" data-id="2celyqlyp" data-path="src/components/ContactSection.tsx"></div>
                      <span data-id="q3zdfdwzk" data-path="src/components/ContactSection.tsx">Enviando...</span>
                    </div> :

                  <div className="flex items-center space-x-2" data-id="t5drsiogj" data-path="src/components/ContactSection.tsx">
                      <Send className="w-5 h-5" data-id="56x0vww5k" data-path="src/components/ContactSection.tsx" />
                      <span data-id="bl3ru7fr5" data-path="src/components/ContactSection.tsx">Enviar Solicitud</span>
                    </div>
                  }
                </Button>
              </form>

              <div className="mt-6 pt-6 border-t border-gray-200" data-id="tgr5v5nt1" data-path="src/components/ContactSection.tsx">
                <p className="text-sm text-gray-600 text-center" data-id="3d2i33yft" data-path="src/components/ContactSection.tsx">
                  Al enviar este formulario, aceptas que nos pongamos en contacto contigo. 
                  <br data-id="1nbio4xz9" data-path="src/components/ContactSection.tsx" />
                  <span className="font-medium" data-id="c141y82pr" data-path="src/components/ContactSection.tsx">Respuesta garantizada en 24 horas.</span>
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Emergency Contact */}
        <div className="mt-16 bg-red-50 border border-red-200 rounded-2xl p-8 text-center" data-id="q0qnn2f86" data-path="src/components/ContactSection.tsx">
          <h3 className="text-2xl font-bold text-red-800 mb-4" data-id="a50ma8uqg" data-path="src/components/ContactSection.tsx">
            🚨 Soporte de Emergencia
          </h3>
          <p className="text-red-700 mb-4" data-id="tf8kjkded" data-path="src/components/ContactSection.tsx">
            ¿Problemas con tu sistema de automatización? Nuestro equipo técnico está disponible 24/7.
          </p>
          <Button className="bg-red-600 hover:bg-red-700 text-white" data-id="spy1tbl46" data-path="src/components/ContactSection.tsx">
            Contactar Soporte de Emergencia
          </Button>
        </div>
      </div>
    </section>);

};

export default ContactSection;